<template>
  <div class="idv-document-response">
    <transition name="idv-document-response__fade">
      <div
        v-if="isLoading"
        class="idv-document-response__loading"
      >
        <div class="idv-document-response__loading-content">
          <div class="idv-document-response__spinner" />
          <h2
            class="idv-document-response__loading-title"
            v-text="$ml.get('idvDocumentResponseLoadingTitle')"
          />
          <p
            class="idv-document-response__loading-description"
            v-text="$ml.get('idvDocumentResponseLoadingDescription')"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import SignatureService from '../../../services/signature.service';

const EVENTS = {
  SIGNING_COMPLETE: 'signing_complete',
  DECLINE: 'decline',
  CANCEL: 'cancel',
  EXCEPTION: 'exception',
  TTL_EXPIRED: 'ttl_expired',
};

const THEMES = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
};

export default {
  name: 'DocumentResponse',

  data() {
    return {
      event: '',
      envelopeId: '',
      userId: '',
      saveLoading: false,
      saveError: false,
      saveAttempted: false,
      isLoading: true,
    };
  },

  computed: {
    isSigningComplete() {
      return this.event === EVENTS.SIGNING_COMPLETE;
    },

    screenState() {
      return this.buildScreenState();
    },
  },

  async mounted() {
    this.getEventFromRoute();

    if(this.programId){
      this.saveSignatureNDA()
    }

    if (this.isSigningComplete && !this.programId) {
      await this.saveSignature();
    }

    this.isLoading = false;
    this.showResultAlert();
  },

  methods: {
    getEventFromRoute() {
      const query = this.$route.query;

      this.event = String(query.event || '').toLowerCase();
      this.envelopeId = query.envelopeId || query.envelope_id || '';
      this.userId = query.userId || query.user_id || '';
      this.programId = query.programId
    },

    buildScreenState() {
      const configs = {
        [EVENTS.SIGNING_COMPLETE]: {
          theme: THEMES.SUCCESS,
          icon: 'success',
          title: this.$ml.get('idvDocumentResponseSigningCompleteTitle'),
          description: this.$ml.get('idvDocumentResponseSigningCompleteDescription'),
        },
        [EVENTS.DECLINE]: {
          theme: THEMES.WARNING,
          icon: 'warning',
          title: this.$ml.get('idvDocumentResponseDeclineTitle'),
          description: this.$ml.get('idvDocumentResponseDeclineDescription'),
        },
        [EVENTS.CANCEL]: {
          theme: THEMES.WARNING,
          icon: 'warning',
          title: this.$ml.get('idvDocumentResponseCancelTitle'),
          description: this.$ml.get('idvDocumentResponseCancelDescription'),
        },
        [EVENTS.EXCEPTION]: {
          theme: THEMES.ERROR,
          icon: 'error',
          title: this.$ml.get('idvDocumentResponseExceptionTitle'),
          description: this.$ml.get('idvDocumentResponseExceptionDescription'),
        },
        [EVENTS.TTL_EXPIRED]: {
          theme: THEMES.WARNING,
          icon: 'warning',
          title: this.$ml.get('idvDocumentResponseTtlExpiredTitle'),
          description: this.$ml.get('idvDocumentResponseTtlExpiredDescription'),
        },
      };

      return configs[this.event] || this.buildFallbackState();
    },

    buildFallbackState() {
      return {
        theme: THEMES.ERROR,
        icon: 'error',
        title: this.$ml.get('idvDocumentResponseFallbackTitle'),
        description: this.$ml.with(this.event).get('idvDocumentResponseFallbackDescription'),
      };
    },

    async saveSignature() {
      if (!this.isSigningComplete || this.saveLoading || this.saveAttempted) {
        return;
      }

      this.saveLoading = true;
      this.saveError = false;

      try {
        await SignatureService.putSignature(this.envelopeId);
        this.saveAttempted = true;
     
      } catch (error) {
        this.saveError = true;
        console.error('Error saving signature:', error);
      } finally {
        this.saveLoading = false;
      }
    },

    async saveSignatureNDA() {
      if (!this.isSigningComplete || this.saveLoading || this.saveAttempted) {
        return;
      }

      this.saveLoading = true;
      this.saveError = false;

      try {
        await SignatureService.putSignatureNDA(this.envelopeId, this.programId);
        this.saveAttempted = true;
     
      } catch (error) {
        this.saveError = true;
        console.error('Error saving signature:', error);
      } finally {
        this.saveLoading = false;
      }
    },


    showResultAlert() {
      const state = this.saveError
        ? {
            icon: 'error',
            title: this.$ml.get('idvDocumentResponseTitleFailed'),
            text: this.$ml.get('idvDocumentResponseSaveErrorMessage'),
          }
        : {
            icon: this.screenState.icon,
            title: this.screenState.title,
            text: this.screenState.description,
          };

      Swal.fire({
        ...state,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        footer: this.$ml.get('idvDocumentResponseRedirectMessage'),
        customClass: {
          popup: 'idv-document-response__swal-popup',
        },
      }).then(() => {
        const acceptedInvite = localStorage.getItem('invitation_id');

        if (acceptedInvite) {
          this.$router.push({
          path: "/program/invitations",
          query: {
            invitationId: acceptedInvite,
          },
        });
        localStorage.removeItem('invitation_id');
        return;
        }
        this.$router.push({
            name: "account",
            params: {
              updatedMy: "updated_my",
            },
          });
      });
    },
  },
};
</script>

<style scoped>
.idv-document-response {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.idv-document-response__loading {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
}

.idv-document-response__loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem;
  max-width: 420px;
}

.idv-document-response__spinner {
  width: 56px;
  height: 56px;
  border: 4px solid rgba(115, 11, 169, 0.12);
  border-top-color: #730ba9;
  border-radius: 50%;
  animation: idv-document-response-spin 1s linear infinite;
}

.idv-document-response__loading-title {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.35rem;
  font-weight: 600;
  color: #202124;
}

.idv-document-response__loading-description {
  margin: 0;
  font-size: 1rem;
  color: #6c757d;
  line-height: 1.5;
}

.idv-document-response__fade-enter-active,
.idv-document-response__fade-leave-active {
  transition: opacity 0.3s ease;
}

.idv-document-response__fade-enter,
.idv-document-response__fade-leave-to {
  opacity: 0;
}

.idv-document-response__swal-popup {
  border-radius: 16px;
  padding: 24px;
}


@keyframes idv-document-response-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
